import { useFetcher } from '@remix-run/react';
import { GQLOperationalSettingsFragment, GQLOrderAndShippingTimesFragment } from '@theprintbar/graphql';
import { WebRoutes } from '@theprintbar/shared';
import { useEffect } from 'react';
import { TimePill } from './TimePill';

export type TimeFrame = {
	title: string;
	text: string;
};

interface OrderAndShippingTimesProps {
	content: GQLOrderAndShippingTimesFragment;
}

const blues = ['bg-blue-bright/20', 'bg-blue-bright/40', 'bg-blue-bright/60', 'bg-blue-bright/80', 'bg-blue-bright'] as const;
const purples = ['bg-purple/20', 'bg-purple/40', 'bg-purple/60', 'bg-purple/80', 'bg-purple'] as const;
const getText = (min: number, max: number) => {
	if (min === max) {
		return `${min} days`;
	}
	return `${min} - ${max} days`;
};

export function OrderAndShippingTimes({ content }: OrderAndShippingTimesProps) {
	const fetcher = useFetcher<GQLOperationalSettingsFragment>();

	useEffect(() => {
		fetcher.load(WebRoutes.API.commerce.operationalSettings.timeframes);
	}, []);

	if (!fetcher.data) return null;

	const { regularShipping, expressShipping, internationalShipping } = fetcher.data;

	return (
		<div className="flex w-full h-full gap-4 lg:gap-8 flex-col lg:flex-row">
			<div className="w-full space-y-5">
				{content.orderAndShippingTimesLeftTitle && <h4 className="text-lg uppercase">{content.orderAndShippingTimesLeftTitle}</h4>}
				<div className="space-y-3">
					<TimePill title="Same Day Prints (Order by 11am)" text="1 day" bgColour={blues[0]} />
					<TimePill
						title="Digital Printing (DTG)"
						text={getText(fetcher.data.dtgMinTurnaround, fetcher.data.dtgMaxTurnaround)}
						bgColour={blues[1]}
					/>
					<TimePill
						title="Direct To Film (DTF)"
						text={getText(fetcher.data.dtfMinTurnaround, fetcher.data.dtfMaxTurnaround)}
						bgColour={blues[2]}
					/>
					<TimePill
						title="Embroidery"
						text={getText(fetcher.data.embMinTurnaround, fetcher.data.embMaxTurnaround)}
						bgColour={blues[4]}
					/>
					<TimePill
						title="Screen Printing"
						text={getText(fetcher.data.scrMinTurnaround, fetcher.data.scrMaxTurnaround)}
						bgColour={blues[3]}
					/>
				</div>
			</div>
			<div className="w-full space-y-5">
				{content.orderAndShippingTimesRightTitle && (
					<h4 className="text-lg uppercase">{content.orderAndShippingTimesRightTitle}</h4>
				)}
				<div className="space-y-3">
					{regularShipping.structure?.name && (
						<TimePill
							title={regularShipping.structure.name}
							text={getText(regularShipping.structure.minTurnaround || 0, regularShipping.structure.maxTurnaround || 0)}
							bgColour={purples[0]}
						/>
					)}
					{expressShipping.structure?.name && (
						<TimePill
							title={expressShipping.structure.name}
							text={getText(expressShipping.structure.minTurnaround || 0, expressShipping.structure.maxTurnaround || 0)}
							bgColour={purples[1]}
						/>
					)}
					{internationalShipping.structure?.name && (
						<TimePill
							title={internationalShipping.structure.name}
							text={getText(
								internationalShipping.structure.minTurnaround || 0,
								internationalShipping.structure.maxTurnaround || 0,
							)}
							bgColour={purples[2]}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
